// src/components/SystemHealthTab.js
// SuperAdmin → System Health tab
// Server uptime/memory, DB status, POS push rates, payment gateway success
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

function StatusPill({ ok, label }) {
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:5,
      background: ok ? '#22c55e22' : '#ef444422',
      color: ok ? '#22c55e' : '#ef4444',
      border: `1px solid ${ok ? '#22c55e' : '#ef4444'}`,
      padding:'3px 10px', borderRadius:20, fontSize:12, fontWeight:600 }}>
      <span style={{ width:7, height:7, borderRadius:'50%',
        background: ok ? '#22c55e' : '#ef4444', display:'inline-block' }} />
      {label}
    </span>
  );
}

function MetricCard({ label, value, sub, accent, wide }) {
  return (
    <div style={{ background:'var(--card)', border:'1px solid var(--border)', borderRadius:10,
      padding:'14px 18px', minWidth: wide ? 200 : 130, flex: wide ? '1 1 200px' : '0 0 auto' }}>
      <div style={{ fontSize:11, color:'var(--muted)', textTransform:'uppercase',
        letterSpacing:'0.06em', marginBottom:6 }}>{label}</div>
      <div style={{ fontSize:22, fontWeight:700, color: accent || 'var(--fg)' }}>{value}</div>
      {sub && <div style={{ fontSize:11, color:'var(--muted)', marginTop:4 }}>{sub}</div>}
    </div>
  );
}

function SectionHead({ title }) {
  return (
    <h3 style={{ margin:'24px 0 12px', fontSize:13, fontWeight:700, color:'var(--muted)',
      textTransform:'uppercase', letterSpacing:'0.06em', borderBottom:'1px solid var(--border)',
      paddingBottom:8 }}>{title}</h3>
  );
}

function GaugeBar({ value, max = 100, color }) {
  const pct = Math.min(100, Math.round((value / max) * 100));
  const c = color || (pct < 60 ? '#22c55e' : pct < 85 ? '#f59e0b' : '#ef4444');
  return (
    <div style={{ background:'var(--border)', borderRadius:4, height:8, width:'100%', maxWidth:180 }}>
      <div style={{ width:`${pct}%`, height:'100%', borderRadius:4, background:c,
        transition:'width 0.4s' }} />
    </div>
  );
}

export default function SystemHealthTab() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [days, setDays]       = useState(1);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.get(`/super-admin/system-health?days=${days}`);
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setLoading(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const s = {
    table: { width:'100%', borderCollapse:'collapse', fontSize:13 },
    th: { padding:'8px 12px', textAlign:'left', color:'var(--muted)', fontWeight:600,
      fontSize:11, textTransform:'uppercase', letterSpacing:'0.05em',
      borderBottom:'1px solid var(--border)', background:'var(--card)' },
    td: { padding:'10px 12px', borderBottom:'1px solid var(--border)' },
  };

  return (
    <div style={{ color:'var(--fg)', fontFamily:'inherit' }}>

      {/* ── Header ── */}
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20, flexWrap:'wrap' }}>
        <h2 style={{ margin:0, fontSize:18, fontWeight:700 }}>System Health</h2>
        <div style={{ display:'flex', gap:8, marginLeft:'auto', alignItems:'center' }}>
          <select value={days} onChange={e => setDays(Number(e.target.value))}
            style={{ background:'var(--card)', color:'var(--fg)', border:'1px solid var(--border)',
              borderRadius:6, padding:'6px 10px', fontSize:13 }}>
            <option value={1}>Last 24h</option>
            <option value={3}>Last 3d</option>
            <option value={7}>Last 7d</option>
            <option value={30}>Last 30d</option>
          </select>
          <button onClick={load} disabled={loading}
            style={{ background:'var(--accent)', color:'#fff', border:'none', borderRadius:6,
              padding:'7px 16px', cursor:'pointer', fontWeight:600, fontSize:13 }}>
            {loading ? '…' : '↺ Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ background:'#ef444422', border:'1px solid #ef4444', borderRadius:8,
          padding:'12px 16px', color:'#ef4444', marginBottom:16 }}>
          ⚠️ {error}
        </div>
      )}

      {!data && !loading && (
        <div style={{ color:'var(--muted)', padding:24 }}>Click Refresh to load health data.</div>
      )}

      {data && (
        <>
          {/* ── Server ── */}
          <SectionHead title="Server" />
          <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:8 }}>
            <MetricCard label="Uptime"    value={data.server.uptimeHuman} sub="PM2 process" />
            <MetricCard label="RSS Memory" value={`${data.server.memoryMB.rss} MB`}
              accent={data.server.memoryMB.rss > 1500 ? '#f59e0b' : undefined} />
            <MetricCard label="Heap Used"  value={`${data.server.memoryMB.heap} MB`}
              sub={`of ${data.server.memoryMB.heapTotal} MB`}
              accent={data.server.memoryMB.heap / data.server.memoryMB.heapTotal > 0.85 ? '#ef4444' : undefined} />
            <MetricCard label="Platform"  value={data.platform.activeVendors}
              sub={`vendors · ${data.platform.activeMalls} mall${data.platform.activeMalls !== 1 ? 's' : ''}`} />
          </div>
          <div style={{ marginBottom:8 }}>
            <div style={{ fontSize:12, color:'var(--muted)', marginBottom:6 }}>
              Heap usage {Math.round((data.server.memoryMB.heap / data.server.memoryMB.heapTotal) * 100)}%
            </div>
            <GaugeBar value={data.server.memoryMB.heap} max={data.server.memoryMB.heapTotal} />
          </div>

          {/* ── Database ── */}
          <SectionHead title="Database" />
          <div style={{ display:'flex', gap:12, alignItems:'center', flexWrap:'wrap', marginBottom:8 }}>
            <StatusPill ok={data.database.status === 'OK'} label={data.database.status} />
            {data.database.latencyMs !== null && (
              <MetricCard label="DB Latency" value={`${data.database.latencyMs} ms`}
                accent={data.database.latencyMs > 200 ? '#f59e0b' : '#22c55e'} />
            )}
          </div>

          {/* ── POS Push ── */}
          <SectionHead title={`POS Push — Last ${days}d`} />
          <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:8 }}>
            <MetricCard label="Total Pushed"  value={data.posPush.total} />
            <MetricCard label="Success"       value={data.posPush.success}
              accent="#22c55e" />
            <MetricCard label="Failed"        value={data.posPush.failed}
              accent={data.posPush.failed > 0 ? '#ef4444' : '#22c55e'} />
            <MetricCard label="Pending"       value={data.posPush.pending}
              accent={data.posPush.pending > 0 ? '#f59e0b' : undefined} />
            <MetricCard label="Fail Rate"     value={`${data.posPush.failRate}%`}
              accent={data.posPush.failRate > 10 ? '#ef4444' : '#22c55e'} />
          </div>
          {data.posPush.total > 0 && (
            <div style={{ marginBottom:8 }}>
              <div style={{ fontSize:12, color:'var(--muted)', marginBottom:6 }}>
                Success rate {100 - data.posPush.failRate}%
              </div>
              <GaugeBar value={data.posPush.success} max={data.posPush.total}
                color={data.posPush.failRate > 10 ? '#ef4444' : '#22c55e'} />
            </div>
          )}

          {/* ── Payment Gateways ── */}
          <SectionHead title={`Payment Gateways — Last ${days}d`} />
          {data.payments.gateways.length === 0 ? (
            <div style={{ color:'var(--muted)', fontSize:13 }}>No payment data in window.</div>
          ) : (
            <div style={{ overflowX:'auto', borderRadius:10, border:'1px solid var(--border)' }}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Gateway</th>
                    <th style={s.th}>Total</th>
                    <th style={s.th}>Success</th>
                    <th style={s.th}>Failed</th>
                    <th style={s.th}>Success Rate</th>
                    <th style={s.th}>Bar</th>
                  </tr>
                </thead>
                <tbody>
                  {data.payments.gateways.map(gw => (
                    <tr key={gw.name}>
                      <td style={{ ...s.td, fontWeight:600 }}>{gw.name}</td>
                      <td style={s.td}>{gw.total}</td>
                      <td style={{ ...s.td, color:'#22c55e' }}>{gw.success}</td>
                      <td style={{ ...s.td, color: gw.failed > 0 ? '#ef4444' : 'inherit' }}>{gw.failed}</td>
                      <td style={s.td}>
                        <span style={{ fontWeight:700,
                          color: gw.successRate >= 95 ? '#22c55e' : gw.successRate >= 80 ? '#f59e0b' : '#ef4444' }}>
                          {gw.successRate}%
                        </span>
                      </td>
                      <td style={s.td}>
                        <GaugeBar value={gw.success} max={gw.total}
                          color={gw.successRate >= 95 ? '#22c55e' : gw.successRate >= 80 ? '#f59e0b' : '#ef4444'} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Order Flow ── */}
          <SectionHead title={`Order Flow — Last ${days}d`} />
          <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
            <MetricCard label="Total Orders" value={data.orderFlow.total} wide />
            {Object.entries(data.orderFlow.byStatus).sort((a,b) => b[1]-a[1]).map(([status, count]) => (
              <MetricCard key={status} label={status}  value={count}
                accent={status === 'CANCELLED' ? '#ef4444' : status === 'DELIVERED' || status === 'COMPLETED' ? '#22c55e' : undefined} />
            ))}
          </div>

          <div style={{ fontSize:11, color:'var(--muted)', marginTop:20 }}>
            Snapshot at {new Date(data.timestamp).toLocaleTimeString()} ·
            Payment window: {days}d · Server uptime: {data.server.uptimeHuman}
          </div>
        </>
      )}
    </div>
  );
}
